import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ElementRef, Inject,
  Input, OnChanges,
  OnDestroy,
  OnInit, PLATFORM_ID, Renderer2, SimpleChanges,
  ViewChild
} from '@angular/core';
import { TournamentWidgetService } from './tournament-widget.service';
import { takeUntil } from 'rxjs/operators';
import {
  componentDestroyed, handleError, Playoff,
  Tournament, TournamentGroup, TournamentRound, TournamentRoundTeam, TournamentRoundTypes,
  UntilDestroy
} from '@mtgame/core';
import { isPlatformBrowser } from '@angular/common';
import { getTournamentRoute } from '@config/routes';
import { ToastService } from '@mtgame/ui';
import { DIVISION_QUERY_PARAM, ROUND_QUERY_PARAM, TOURNAMENT_QUERY_PARAM } from '@config/routing-map';

@UntilDestroy
@Component({
  selector: 'mtg-tournament-widget',
  templateUrl: './tournament-widget.component.html',
  styleUrls: ['./tournament-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TournamentWidgetService,
  ]
})
export class TournamentWidgetComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  tournament: Tournament;
  @Input()
  titleVisible = true;
  loading = true;
  roundLoading = false;

  rounds: TournamentRound[] = [];
  round: TournamentRound;
  roundTypes = TournamentRoundTypes;
  isBrowser = isPlatformBrowser(this.platformId);

  groups: TournamentGroup[] = [];
  divisionGroups: {[key: number]: TournamentGroup[]} = {};
  divisionPlayoffs: {[key: number]: Playoff[]} = {};
  playoffs: Playoff[] = [];

  @ViewChild('mobileSlider', {read: ElementRef})
  mobileSlider: ElementRef;
  slideWidth = 290;
  currentSlide = 1;
  subscriptions = [];
  ignoreScroll = false;
  scrollEndTimeout;

  get tournamentRoute(): string[] {
    return getTournamentRoute(this.tournament.alias);
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private tournamentsWidgetService: TournamentWidgetService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private renderer2: Renderer2,
  ) {
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.tournament && this.tournament) {
      this.loading = true;
      await this.tournamentsWidgetService.initialize(this.tournament);
      this.loading = false;

      setTimeout(() => {
        this.initializeSlider();
      });
      this.cdr.markForCheck();
    }
  }

  async ngOnInit(): Promise<void> {
    if (!this.isBrowser) {
      return;
    }
    this.tournamentsWidgetService.rounds$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(rounds => {
        this.rounds =  rounds;
        this.cdr.markForCheck();
      });

    this.tournamentsWidgetService.selectedRound$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(round => {
        this.round = round;
        this.cdr.markForCheck();
      });

    this.tournamentsWidgetService.playoffs$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(playoffs => {
        this.playoffs = playoffs;
        this.divisionPlayoffs = playoffs.reduce((acc, item) => ({
          ...acc,
          [item.divisionId]: [
            ...(acc[item.divisionId] || []),
            item
          ]
        }), {});
        this.cdr.markForCheck();
      });

    this.tournamentsWidgetService.groups$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(groups => {
        this.groups = groups;
        this.divisionGroups = groups.reduce((acc, item) => ({
          ...acc,
          [item.divisionId]: [
            ...(acc[item.divisionId] || []),
            item
          ]
        }), {});
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    for (const unsubscribe of this.subscriptions) {
      unsubscribe();
    }
    this.tournamentsWidgetService.dispose();
  }

  private initializeSlider(): void {
    if (!this.mobileSlider) {
      return;
    }
    this.subscriptions.push(
      this.renderer2.listen(this.mobileSlider.nativeElement, 'scroll', event => {
        if (this.ignoreScroll) {
          return;
        }

        clearTimeout(this.scrollEndTimeout);
        const currentSlide = Math.round((window.innerWidth + event.target.scrollLeft) / this.slideWidth);
        if (currentSlide !== this.currentSlide) {
          this.currentSlide = currentSlide;
          this.cdr.markForCheck();
        }

        this.scrollEndTimeout = setTimeout(() => {
          this.scroll();
        }, 50);
      })
    );
  }

  slideLeft(): void {
    if (this.currentSlide === 1) {
      return;
    }
    this.currentSlide--;
    this.scroll();
  }

  slideRight(): void {
    if (this.currentSlide === this.round.divisions.length) {
      return;
    }
    this.currentSlide++;
    this.scroll();
  }

  scroll(): void {
    this.ignoreScroll = true;
    const scrollLeft = (this.currentSlide - 1) * 280;
    this.mobileSlider.nativeElement.scroll({left: scrollLeft, top: 0, behavior: 'smooth'});
    this.cdr.markForCheck();
    setTimeout(() => {
      this.ignoreScroll = false;
    }, 500);
  }

  async setRound(round: TournamentRound): Promise<void> {
    this.roundLoading = true;
    try {
      await this.tournamentsWidgetService.setRound(round);
    } catch (e) {
      handleError(e);
      this.toastService.danger('Ошибка загрузки этапа');
    } finally {
      this.roundLoading = false;
      this.cdr.markForCheck();
    }
  }

  getRoundQueryParams(roundId: number) {
    return {[ROUND_QUERY_PARAM]: roundId, [TOURNAMENT_QUERY_PARAM]: this.tournament.id};
  }

  getDivisionQueryParams(divisionId: number) {
    return {[DIVISION_QUERY_PARAM]: divisionId};
  }
}
